import React from 'react';
import { ArrowLeft, CaretLeft, CaretRight } from '../../design-system/icons';
import { useCampaignEditRequestHistoryQuery } from '@discovr/features';
import type { Campaign } from '@discovr/types';
import { Button } from '../../components/ui/button';
import { CampaignDetailsCards } from './CampaignDetailsCards';
import { buildDiffMap, buildFieldHistoryMap } from './editRequestDiff';
import { formatRelativeTime, describeRequestSubmitters } from './editRequestFormat';

interface CampaignChangesHistoryViewProps {
  campaign: Campaign;
  campaignId: string;
  onClose: () => void;
}

/** Brand-side "Changes history" — same Campaign Details / Brief layout as
 *  the live view, but overlaid with a selected past edit request's diff
 *  (including already-applied fields, unlike the admin's always-on current-
 *  request overlay — this IS the historical record). Defaults to the most
 *  recent request; Older/Newer steps through the rest. */
export function CampaignChangesHistoryView({ campaign, campaignId, onClose }: CampaignChangesHistoryViewProps) {
  const { data: history, isLoading } = useCampaignEditRequestHistoryQuery(campaignId, true);
  const [index, setIndex] = React.useState(0);

  const selected = history?.[index];
  const diffMap = selected ? buildDiffMap(selected, { includeApplied: true }) : new Map();
  const fieldHistory = selected && history ? buildFieldHistoryMap(history, selected.id) : new Map();
  const resolvedCount = selected?.changes.filter((c) => c.status !== 'pending').length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="mb-1 inline-flex items-center gap-1.5 text-body-sm-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={14} />
            Back to details
          </button>
          <h2 className="text-heading-h5 font-semibold text-foreground">Changes history</h2>
          <p className="mt-1 text-body-sm-regular text-muted-foreground">
            Every batch of changes requested on this campaign, and what happened to each.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-body-sm-regular text-muted-foreground">Loading…</p>}

      {!isLoading && (!history || history.length === 0) && (
        <div className="rounded-lg border border-border bg-muted px-4 py-3 text-body-sm-regular text-muted-foreground">
          No changes have been requested yet.
        </div>
      )}

      {selected && history && (
        <>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-body-sm-regular text-muted-foreground">
              {selected.changes.length > 0 && (
                <>
                  {describeRequestSubmitters(selected.changes)}
                  {' · '}
                  {formatRelativeTime(selected.submitted_at)}
                  {' · '}
                </>
              )}
              <span className="capitalize">{selected.status}</span>
              {' · '}
              {selected.changes.length} field{selected.changes.length === 1 ? '' : 's'}
              {resolvedCount > 0 && ` (${resolvedCount} decided)`}
            </div>
            {history.length > 1 && (
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={index >= history.length - 1}
                  onClick={() => setIndex((i) => i + 1)}
                  className="gap-1"
                >
                  <CaretLeft size={12} weight="bold" />
                  Older
                </Button>
                <span className="text-caption-regular text-muted-foreground">
                  {index + 1} / {history.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={index <= 0}
                  onClick={() => setIndex((i) => i - 1)}
                  className="gap-1"
                >
                  Newer
                  <CaretRight size={12} weight="bold" />
                </Button>
              </div>
            )}
          </div>

          <CampaignDetailsCards campaign={campaign} diffMap={diffMap} requestId={selected.id} fieldHistory={fieldHistory} audience="brand" />
        </>
      )}
    </div>
  );
}
